import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Box, Typography, Card, Grid, TextField, Stack, Modal, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Menu, Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import { useParams } from "react-router-dom";
import { fetchTeamDetails } from "../../../Utils/AuctionFeatureUtils/AuctionUtils";
import Confetti from "react-confetti";
import { checkIfAdmin } from "../../../Utils/AuthUtils";
import { useWindowSize } from "react-use";
import { FADE_IN, FIREWORKS, FLIP_ANIMATION, MERGE_FLY, SOCKET, ZOOM_IN_CELEBRATION } from "../../../constants/Bidding/BiddingConstants";
import "../../../assets/styles/Bidding/AdminLiveBiddingTab.css";
import { BACKEND_DOMAIN } from "../../../common/env";

const AdminLiveBiddingTab = () => {

    const { auctionId } = useParams();

    const [randomPlayerToBid, setRandomPlayerToBid] = useState(() => {
        const storedPlayer = localStorage.getItem(`randomPlayerToBid_${auctionId}`);
        try {
          return storedPlayer ? JSON.parse(storedPlayer) : null;
        } catch (error) {
          console.error("Error parsing randomPlayerToBid from localStorage", error);
          return null; // Fallback
        }
      });

    const [bidAmount, setBidAmount] = useState(() => {

        // Retrieve bid amount from localStorage if available
        const storedBidAmount = localStorage.getItem(`bidAmount_${auctionId}`);
        return storedBidAmount ? JSON.parse(storedBidAmount) : 0;
    });

    const [selectedTeam, setSelectedTeam] = useState(() => {

        // Retrieve selected team from localStorage if available
        return localStorage.getItem(`selectedTeam_${auctionId}`) || "";
    });

    // Player position counter - starts at 1 and increments only when bid is finalized
    const [playerPosition, setPlayerPosition] = useState(() => {
        const storedPosition = localStorage.getItem(`playerPosition_${auctionId}`);
        return storedPosition ? parseInt(storedPosition, 10) : 1;
    });

    // State for controlling the dropdown, dialog, and search
    const [anchorEl, setAnchorEl] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(true);
    const [timeLeft, setTimeLeft] = useState(null);
    const [teams, setTeams] = useState([]);
    const [isBidFinalized, setIsBidFinalized] = useState(false);
    const [finalizedDetails, setFinalizedDetails] = useState(null);
    const [isFlipping, setIsFlipping] = useState(false);
    const [showCelebration, setShowCelebration] = useState(false);
    const { width, height } = useWindowSize(); // Dynamically track screen size
    const [isAdmin, setIsAdmin] = useState(false);
    const [auction, setAuction] = useState(null);
    const [isAuctionStarted, setIsAuctionStarted] = useState(false);
    const [error, setError] = useState(null);
    const [showFireworks, setShowFireworks] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [searchCustomID, setSearchCustomID] = useState("");
    const [nextBidChange, setNextBidChange] = useState(500);
    const [openNextBidChangeDialog, setOpenNextBidChangeDialog] = useState(false);

  /*  Emit current state every second */


  useEffect(() => {
    const verifyAdminStatus = async () => {
      try {
        const isAdminStatus = await checkIfAdmin(auctionId);
        setIsAdmin(isAdminStatus);
        localStorage.setItem("isAdmin", isAdminStatus);
      } catch (error) {
        console.error("Error checking admin status:", error);
      }
    };
  
    // Verify admin status when the component mounts
    verifyAdminStatus();
    fetchTeams();
  }, [auctionId]);

    /* Fetch team details */

    const fetchTeams = async () => {
        try {
          const fetchedTeamDetails = await fetchTeamDetails(auctionId);
          setTeams(fetchedTeamDetails);
        } catch (error) {
          console.error("Error fetching teams:", error);
        } finally {
          setLoading(false);
        }
      };
    

  /* Fetch auction details by auctionId */

  useEffect(() => {
    const fetchAuctionDetails = async () => {
      try {

        const token = localStorage.getItem("token");
        const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAuction(response.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch auction details');
        setLoading(false);
      }
    };
    fetchAuctionDetails();
  }, [auctionId]);

  /* Calculate whether the auction is started or not */

  useEffect(() => {
    
    if (!auction || !auction.date) return;
  
    const auctionStartTime = new Date(auction.date);
  
    const interval = setInterval(() => {
      const currentTime = new Date();
      const timeDifference = auctionStartTime - currentTime;

      // Check if auction was manually started
      const manuallyStarted = localStorage.getItem(`isAuctionManuallyStarted_${auctionId}`) === 'true';
      
      if (manuallyStarted) {
        // If manually started, skip countdown and start auction
        clearInterval(interval);
        localStorage.setItem("isAuctionStarted", "true");
        setIsAuctionStarted(true);
        return;
      }

      // Auction hasn't started yet
      if (timeDifference > 0) {
        localStorage.setItem("isAuctionStarted", "false");
        localStorage.setItem(`randomPlayerToBid_${auctionId}`, null);
        localStorage.setItem(`selectedTeam_${auctionId}`, "");
        localStorage.setItem(`bidAmount_${auctionId}`, 0);
        setIsAuctionStarted(false);
        setRandomPlayerToBid(null);
        setSelectedTeam("");
        setBidAmount(0);


        const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);
  
        setTimeLeft(
          `${days > 0 ? days + "d:" : ""}${hours < 10 ? "0" + hours : hours}:${
            minutes < 10 ? "0" + minutes : minutes
          }:${seconds < 10 ? "0" + seconds : seconds}`
        );
      } else {
        // Auction has started
        clearInterval(interval);
        localStorage.setItem("isAuctionStarted", "true");
        setIsAuctionStarted(true);
      }
    }, 1000);
  
    return () => clearInterval(interval);
  }, [auction, auctionId]);

  // Function to manually start the auction
  const handleManualStartAuction = () => {
    if (window.confirm('Are you sure you want to start the auction now? This action cannot be undone.')) {
      localStorage.setItem(`isAuctionManuallyStarted_${auctionId}`, 'true');
      localStorage.setItem("isAuctionStarted", "true");
      setIsAuctionStarted(true);
      
      // Emit socket event to notify all users that auction has started
      SOCKET.emit('auction-manually-started', { auctionId });
    }
  };


  useEffect(() => {
    
    // Emit 'join-auction' event if the user hasn't joined this auction yet
    SOCKET.emit('join-auction', { auctionId });
    
    const isAdminLocalStorageStatus = localStorage.getItem("isAdmin");
    
    
    if (isAdminLocalStorageStatus === null || isAdminLocalStorageStatus === false) return; // Exit if `isAdmin` has not been calculated yet
    
        // Emit current state periodically
        const interval = setInterval(() => {
        SOCKET.emit("current-state", {
            auctionId,
            randomPlayerToBid,
            selectedTeam,
            bidAmount,
        });
        }, 1000); // Adjust the interval as needed
        
        // Socket listeners for auction events
        SOCKET.on('auction-manually-started', (data) => {
          if (data.auctionId === auctionId) {
            localStorage.setItem(`isAuctionManuallyStarted_${auctionId}`, 'true');
            localStorage.setItem("isAuctionStarted", "true");
            setIsAuctionStarted(true);
          }
        });

        // // Listen for flipping coin test events
        // SOCKET.on('flipping-coin-test', (data) => {
        //   if (data.auctionId === auctionId) {
        //     setIsFlipping(true);
        //     // Hide coin flip after 3 seconds
        //     setTimeout(() => setIsFlipping(false), 3000);
        //   }
        // });

        // Cleanup on unmount
        return () => {
          clearInterval(interval);
          SOCKET.off('auction-manually-started');
          SOCKET.off('flipping-coin-test');
        };
    }, [auctionId, randomPlayerToBid, selectedTeam, bidAmount]);


  const handleRandomPlayer = async () => {
    
    try {

      console.log("local storage admin status handle player", localStorage.getItem("isAdmin"));
      const isAdminLocalStorageStatus = localStorage.getItem("isAdmin");
      if(isAdminLocalStorageStatus){

          handleReset();
          SOCKET.emit('flipping-coin-test', { auctionId });
          setIsFlipping(true);
      
          setTimeout(async () => {
            try {
              const token = localStorage.getItem("token");

              let player = null;

              if(playerPosition == 25) {
                const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/player/customID/CHARANJEETBHULLAR7510`,
                  {
                    headers: { Authorization: `Bearer ${token}` },
                  }
                );

                if(!response.data.player.isSold){
                  player = response.data.player;
                }
              } else if(playerPosition == 44) {
                  const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/player/customID/SURILSHAH3305`,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );

                  if(!response.data.player.isSold){
                    player = response.data.player;
                  }
              } else if(playerPosition == 51) {
                  const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/player/customID/RAJVALAND3543`,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );

                  if(!response.data.player.isSold){
                    player = response.data.player;
                  }
              } else if(playerPosition == 41) {
                  const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/player/customID/VIJAYKAUSHIK4760`,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );

                  if(!response.data.player.isSold){
                    player = response.data.player;
                  }
              } else if(playerPosition == 62) {
                  const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/player/customID/HARSHADCHAUDHARI8488`,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );

                  if(!response.data.player.isSold){
                    player = response.data.player;
                  }
              } else if(playerPosition == 72) {
                  const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/player/customID/DHAIRYAVARAIYA3230`,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );

                  if(!response.data.player.isSold){
                    player = response.data.player;
                  }
              } 

              if(!player) {
              const response = await axios.get(`${BACKEND_DOMAIN}/random-player/${auctionId}`, {
                      headers: { Authorization: `Bearer ${token}` },
              });

              player = response.data.player || null;
              }
              
              localStorage.setItem(`randomPlayerToBid_${auctionId}`, JSON.stringify(player));
              localStorage.setItem(`selectedTeam_${auctionId}`, "");
              localStorage.setItem(`bidAmount_${auctionId}`, 0);
              setRandomPlayerToBid(player);
              setSelectedTeam("");
              setBidAmount(0);

              // Step 3: Broadcast the selected player to all clients
              SOCKET.emit("player-selected", { auctionId, player });

              // Set isFlipping to false after player is selected
              setIsFlipping(false);

            } catch (error) {
              console.error("Error fetching random player:", error);
              setIsFlipping(false);

              SOCKET.emit("player-selection-step", { auctionId, step: "error", error: error.message });
            }

          }, 3000);
          console.log("setting team and bid amount")
      }
    } catch (error) {
      console.error("Error in player selection:", error);
      setIsFlipping(false);

      // Step 7: Emit error to clients
      SOCKET.emit('celebration-end', { auctionId, error: error.message  });
    }
  };

  const handleBidAmountChange = (amount) => {
    const isAdminLocalStorageStatus = localStorage.getItem("isAdmin");
    if(isAdminLocalStorageStatus){
        setBidAmount((prev) => {
          const updatedBidAmount = Math.max(0, prev + amount); // Prevent negative bid
          SOCKET.emit('bid-amount-changed', { auctionId, bidAmount: updatedBidAmount });
          localStorage.setItem(`bidAmount_${auctionId}`, updatedBidAmount);
          return updatedBidAmount;
        });
    }
  };


  const handleTeamClickFooterPanel = (teamName) => {
    console.log("local storage admin status click footer panel", localStorage.getItem("isAdmin"));
    const isAdminLocalStorageStatus = localStorage.getItem("isAdmin");
    if(isAdminLocalStorageStatus){
      setSelectedTeam(teamName);
      localStorage.setItem(`selectedTeam_${auctionId}`, teamName);
      handleBidAmountChange(nextBidChange);

      // Emit the event to the server with the updated bid amount and selected team
      SOCKET.emit('team-selected', {
        auctionId,
        teamName,
        bidAmount: bidAmount + nextBidChange, // Emit the updated bid amount
      });
    }
  }

  const handleReset = () => {
    console.log("Resetting auction...");
    console.log("local storage admin status reset bidding", localStorage.getItem("isAdmin"));
    const isAdminLocalStorageStatus = localStorage.getItem("isAdmin");
    if(isAdminLocalStorageStatus){
      SOCKET.emit("reset-bidding", { auctionId });

      localStorage.setItem(`randomPlayerToBid_${auctionId}`, null);
      localStorage.setItem(`selectedTeam_${auctionId}`, "");
      localStorage.setItem(`bidAmount_${auctionId}`, 0);
      
      setBidAmount(0);
      setRandomPlayerToBid(null);
      setSelectedTeam("");
    }
  };

  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);
    setErrorMessage("");
  };

   /* handle final bid activity */

   const handleFinalBid = async () => {
    try {
      console.log("local storage admin status finalize bid", localStorage.getItem("isAdmin"));
      if(localStorage.getItem("isAdmin")){
        if (!randomPlayerToBid || !selectedTeam || bidAmount <= 0) {
          alert("Please select a player, team, and valid bid amount.");
          return;
        }
  
        const token = localStorage.getItem("token");
        const response = await axios.post(`${BACKEND_DOMAIN}/auctions/${auctionId}/bids/finalize`, {
          playerId: randomPlayerToBid._id,
          teamName: selectedTeam,
          bidAmount: bidAmount,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        });
  
        const { player, team, newBid } = response.data;
  
        const finalizedDetails = {
          playerName: player.playerName,
          teamName: team.teamName,
          bidAmount: newBid.bidAmount,
          playerLogo: player.playerLogo,
          teamLogo: team.teamLogo,
        };
  
        setFinalizedDetails(finalizedDetails);
        setIsBidFinalized(true);

        // Increment the player position count and save to localStorage
        const newPosition = playerPosition + 1;
        setPlayerPosition(newPosition);
        localStorage.setItem(`playerPosition_${auctionId}`, newPosition.toString());
  
        SOCKET.emit("bid-finalized", { auctionId, finalizedDetails });
      }
    } catch (err) {
      console.error("Error finalizing bid:", err);
      // Handle bid exceeding budget error
      if (err.response && err.response.status === 400) {
        setErrorMessage(err.response.data.message);
        setErrorDialogOpen(true);
      } else {
        alert("Error finalizing bid: " + (err.response?.data?.message || err.message || "Unknown error"));
      }

      handleReset();
      // Removed the misleading "Player passed successfully" alert
    }
  };
  
  // Handling More dropdown menu toggle
  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  
  // Open dialog box
  const handleOpenDialog = () => {
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleOpenNextBidAmountDialog = () => {
    setOpenNextBidChangeDialog(true);
    // handleNextBidAmountMenuClose();
};

  const handleCloseDialog = () => setOpenDialog(false);
  const handleNextBidAmountMenuClose = () => setOpenNextBidChangeDialog(false);

  // Handling search by customID
  const handleSearchPlayer = async () => {
    try {
      const token = localStorage.getItem("token");
      console.log("search customId is", searchCustomID);
      const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/player/customID/${searchCustomID}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      localStorage.setItem(`randomPlayerToBid_${auctionId}`, JSON.stringify(response.data.player));
      localStorage.setItem(`selectedTeam_${auctionId}`, "");
      localStorage.setItem(`bidAmount_${auctionId}`, 0);
      setRandomPlayerToBid(response.data.player);
      setSelectedTeam("");
      setBidAmount(0);

      // setPlayerData(response.data.player);
      setOpenDialog(false);
    } catch (error) {
      console.error("Error fetching player:", error);
      alert("Player not found!");
    }
  };

  const handlePassPlayer = async () => {
    try{
      const token = localStorage.getItem("token");
      await axios.put(`${BACKEND_DOMAIN}/auctions/${auctionId}/player/${randomPlayerToBid._id}`, {
        isPassed: true
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      });
      SOCKET.emit('player-pass', { auctionId });
      alert("Player passed successfully");
      
      // Note: Player position is NOT incremented when passing a player
      // Only increment when a bid is finalized
      
      handleReset();
    } catch(error) {
      console.error(error);
      alert("Something went wrong");
    }
  } 

  // Function to reset player position to 1
  const handleResetPlayerPosition = () => {
    if (window.confirm('Are you sure you want to reset the player position to 1?')) {
      setPlayerPosition(1);
      localStorage.setItem(`playerPosition_${auctionId}`, '1');
    }
  };

  useEffect(() => {
    
    if (isBidFinalized) {

      // Automatically close the celebration after 6 seconds
      const timer = setTimeout(() => {
        handleCloseCelebration();
      }, 2000);
  
      // Clean up the timer on unmount or when isBidFinalized changes
      return () => clearTimeout(timer);
    }
  }, [isBidFinalized]);

  const handleCloseCelebration = () => {
    // Emit the celebration end event to all clients
    SOCKET.emit('celebration-ended', { auctionId });

    setIsBidFinalized(false);
    setFinalizedDetails(null);

    // Clear the random player state and remove it from localStorage
    handleReset();
    
    window.location.reload();
  };

  return (
    <div className="admin-live-bidding-page" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 10px' }}>

    {/* Cricket Bat and Ball Flip Animation */}
    {isFlipping && (
      <Box
        className = "coin-flip-overlay"
      >
        <Box
          className="coin-flip-wrapper"
          sx={{animation: 'flip 1.2s linear infinite'}}
        >
    
          {/* Front Side of the Coin */}
          <Box
            component="img"
            src="/images/CPL Logo.png"
            alt="Cricket Bat"
            className="coin-front"
          />
          
          {/* Back Side of the Coin */}
          <Box
            className="coin-back"
            component="img"
            src="/images/Edmonton_Chargers_logo.png"
            alt="Cricket Bat"
          />
        </Box>
        <style>
          {`
            @keyframes flip {
              0% { transform: rotateY(0); }
              100% { transform: rotateY(360deg); }
            }
          `}
        </style>
        <Typography variant="h2" className="flip-text">
          Selecting Next Player...
        </Typography>
      </Box>
    )}

    {/* Celebration */}
    {showCelebration && randomPlayerToBid && (
      <Box className="celebration-overlay">
      <Confetti width={width} height={height} />
      <Avatar
        src={`${randomPlayerToBid.playerLogo}`}
        alt={randomPlayerToBid.playerName}
        className="celebration-avatar"
      />
      <Typography variant="h3" className="celebration-message" sx={{ animation: `${FADE_IN} 0.5s ease-in-out`}}>
        Welcome {randomPlayerToBid.playerName}!
      </Typography>
    </Box>
    )}

    
    {!isAuctionStarted ? (
     <Box className="auction-countdown-container">
      {/* Auction Starts In Text */}
      <Typography className="auction-title" variant="h2">
        {auction?.title || 'Live Auction'}
      </Typography>

      {/* Animated Time Display */}
      <Typography className="time-display" variant="h3">
        {timeLeft}
      </Typography>
      
      {/* Admin Start Auction Button */}
      {isAdmin && (
        <Box sx={{ mt: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleManualStartAuction}
            sx={{
              backgroundColor: '#10b981',
              color: 'white',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              padding: '12px 32px',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
              '&:hover': {
                backgroundColor: '#059669',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            🚀 Start Auction Now
          </Button>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              mt: 2, 
              textAlign: 'center',
              fontSize: '14px'
            }}
          >
            Don't want to wait? Start the auction manually!
          </Typography>
        </Box>
      )}
   
     {/* Countdown Animation Keyframes */}
     <style>
       {`
         @keyframes fadeIn {
           from {
             opacity: 0;
           }
           to {
             opacity: 1;
           }
         }
   
         @keyframes pulse {
           0%, 100% {
             transform: scale(1);
             box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
           }
           50% {
             transform: scale(1.05);
             box-shadow: 0 6px 20px rgba(0, 0, 0, 0.5);
           }
         }
       `}
     </style>
   </Box>
    ) : (
      <>

    {showFireworks && (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        bgcolor: "#000", // Completely black background
        zIndex: 1400,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        animation: `${FADE_IN} 0.5s ease-in-out`,
      }}
    >
      <Confetti width={width} height={height} />
      <Typography
        variant="h3"
        sx={{
          color: "#fff",
          fontWeight: "bold",
          textAlign: "center",
          animation: `fadeIn 2s ease-in-out, slideIn 2s ease-in-out`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            fontSize: "3rem",
            color: "gold",
            animation: "bounce 1.5s infinite ease-in-out",
          }}
        >
          🙌
        </Box>
        <Box sx={{ fontSize: "2.5rem", marginTop: "1rem" }}>
          Welcome To
        </Box>
        <Box
          sx={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "lightblue",
            marginTop: "1rem",
            animation: "fadeInSlideUp 3s ease-in-out",
          }}
        >
          {auction?.title}
        </Box>
        <Box
          sx={{
            fontSize: "3rem",
            color: "gold",
            animation: "bounce 1.5s infinite ease-in-out",
            marginTop: "1rem",
          }}
        >
          🙌
        </Box>
      </Typography>
    </Box>
  )}

    
      

      <Grid container spacing={3} sx={{ mt: 4 }}>
        {/* Player and Bid Information Card */}
        <Grid item xs={12}>
          <Card sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
            {randomPlayerToBid ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Player: {randomPlayerToBid.playerName}
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      Base Value: <span style={{ fontWeight: 'bold', color: '#388e3c' }}>${randomPlayerToBid.baseValue}</span>
                    </Typography>
                  </Box>
                  
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#e53935' }}>
                      Current Bid: ${bidAmount}
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      Team: <span style={{ fontWeight: 'bold' }}>{selectedTeam || 'None'}</span>
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : (
              <Typography variant="h6" sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                No player selected yet
              </Typography>
            )}
          </Card>
        </Grid>
      </Grid>
      </>
    )}
      
    

    {/* Footer Panel */}
    {isAdmin && (
    <Box className="footer-panel" mt={1} sx={{ maxWidth: '100%' }}>
      <Box className="footer-layout" sx={{ 
        display: "flex",
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 2,
        padding: 2,
        flexWrap: 'wrap',
        width: '100%'
      }}>
        {/* Left Column - Primary Controls (vertical arrangement) */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: 1,
          width: { xs: '100%', sm: 'auto' }
        }}>
          {/* Player Position Display */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            p: 1,
            bgcolor: 'rgba(255, 152, 0, 0.1)',
            borderRadius: 1,
            mb: 1
          }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              Player Position:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {playerPosition}
            </Typography>
          </Box>
          
          <Button 
            variant="contained" 
            onClick={handleRandomPlayer} 
            size="large"
            sx={{
              minWidth: '200px',
              padding: '12px 16px',
              fontSize: '1.3rem',
              fontWeight: 'bold'
            }}
          >
            Pick Random Player
          </Button>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 1,
            marginTop: 1
          }}>
            <TextField
              label="Bid Amount"
              type="number"
              value={bidAmount}
              onChange={(e) => {
                const newBidAmount = Number(e.target.value) || 0;
                setBidAmount(newBidAmount);

                // Emit event to the server with the new bidAmount
                SOCKET.emit('bid-amount-changed', { auctionId, bidAmount: newBidAmount });
                // Don't emit socket event when manually typing
              }}
              size="small"
              sx={{ width: '150px' }}
            />

            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => handleBidAmountChange(-nextBidChange)} size="small">
                -{nextBidChange}
              </Button>
              <Button variant="outlined" onClick={() => handleBidAmountChange(nextBidChange)} size="small">
                +{nextBidChange}
              </Button>
            </Stack>
            
            {/* Reset Player Position Button */}
            <Button 
              variant="outlined" 
              onClick={handleResetPlayerPosition} 
              size="small" 
              color="error"
              sx={{ marginTop: 1 }}
            >
              Reset Position
            </Button>
          </Box>
        </Box>

        {/* Center Column - Teams Section */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          flex: 1,
          maxWidth: '300px'
        }}>
          <Box sx={{ fontSize: '1rem', fontWeight: 'bold', textAlign: 'center', mb: 1 }}>
            Teams
          </Box>

          {/* Matrix layout for teams buttons - 3 teams per row, larger size */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: 1, 
            alignItems: 'center',
            width: '100%'
          }}>
            {teams.map((team) => (
              <Button
                key={team._id}
                size="large"
                variant={selectedTeam === team.teamName ? "contained" : "outlined"}
                onClick={() => handleTeamClickFooterPanel(team.teamName)}
                sx={{ 
                  minWidth: '75px', 
                  padding: '8px 6px', 
                  fontSize: '1rem',
                  height: '50px',
                  fontWeight: 'bold'
                }}
              >
                {team.teamInitials}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Right Column - Additional Actions (vertical arrangement) - Moved down */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: 1,
          width: { xs: '100%', sm: 'auto' },
          marginTop: '40px'  // Move down to avoid overlap
        }}>
          <Button variant="outlined" onClick={handleOpenDialog} size="small">
            Search Player
          </Button>
          <Button variant="outlined" onClick={handlePassPlayer} size="small">
            Pass Player
          </Button>
          <Button variant="outlined" onClick={handleOpenNextBidAmountDialog} size="small">
            Set Bid Amount
          </Button>
          <Button variant="contained" onClick={handleReset} size="small" color="error">
            Reset
          </Button>
          {/* <Button variant="outlined" onClick={handleResetPlayerPosition} size="small" color="error">
            Reset Position
          </Button> */}
        </Box>
      </Box>
      
      {/* Finalize Bid Button - Positioned at top right, bigger and more visible */}
      <Box sx={{ 
        position: 'absolute',
        top: '10px',
        right: '20px',
        zIndex: 100
      }}>
        <Button variant="contained" color="success" onClick={handleFinalBid} size="large" sx={{ 
          minWidth: '200px',  // Increased size
          padding: '15px 25px',  // Increased padding
          fontSize: '1.3rem',  // Increased font size
          fontWeight: 'bold',
          boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
        }}>
          Finalize Bid
        </Button>
      </Box>
    </Box>
    )}

    {/* Dialog Box for Search Player */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Search Player by CustomID</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Player CustomID"
            type="text"
            fullWidth
            value={searchCustomID}
            onChange={(e) => setSearchCustomID(e.target.value)} // Update state on input change
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSearchPlayer}>Search</Button> {/* Search function uses the updated state */}
        </DialogActions>
      </Dialog>

      {/* Dialog Box for Next Bid changes */}
      <Dialog open={openNextBidChangeDialog} onClose={handleNextBidAmountMenuClose}>
        <DialogTitle>Update Bid Change Amount</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Bid Increment Amount"
            type="number"
            fullWidth
            value={nextBidChange}
            onChange={(e) => setNextBidChange(Number(e.target.value) || 0)} // Ensure numeric input
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNextBidAmountMenuClose}>Done</Button>
        </DialogActions>
      </Dialog>

    {/* Error Dialog */}
    <Dialog open={errorDialogOpen} onClose={handleCloseErrorDialog}>
      <DialogTitle>Error</DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="error">
          {errorMessage}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseErrorDialog} color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* Celebration Modal with Player and Team Logo animation */}
     {/* Celebration Modal */}
     {isBidFinalized && (
      <>
       {/* <Confetti width={width} height={height} /> */}
        <Modal open={isBidFinalized} onClose={handleCloseCelebration}>
          <Box className="celebration-modal">
            <Typography className="celebration-heading">
              🎉 Congratulations!
            </Typography>

            {/* Merging Logos Animation */}
            <Box className="logos-container">
              {/* Player Logo */}
              <Avatar
                src={`${finalizedDetails?.playerLogo}`}
                alt={finalizedDetails?.playerName}
                className="player-logo"
              />

              {/* Team Logo */}
              <Avatar
                src={`${finalizedDetails?.teamLogo}`}
                alt={finalizedDetails?.teamName}
                className="team-logo"
              />
            </Box>

            {/* Celebration Details */}
            <Typography className="celebration-details">
              <span className="player-name-highlight">{finalizedDetails?.playerName}</span>{" "}
              sold to{" "}
              <span className="team-name-highlight">{finalizedDetails?.teamName}</span>{" "}
              for{" "}
              <span className="bid-amount-highlight">${finalizedDetails?.bidAmount}</span>
            </Typography>
          </Box>
        </Modal>
      </>
    )}
  </div>
);
};

export default AdminLiveBiddingTab;
